'use client'

import Loader from '@/app/components/loader'
import { useGiphyFetch } from '@/app/util/use-gf'
import type { IGif } from '@giphy/js-types'
import { Grid as SDKGrid } from '@giphy/react-components'
import { type SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react'
import type { EmbedGif as EmbedGifType } from '../helpers'

const relatedGifLimit = 6
const getRelatedGifLimit = (offset: number) => (offset === 0 ? relatedGifLimit : relatedGifLimit * 2)

function useElementWidth<T extends HTMLElement>(fallbackWidth: number) {
    const ref = useRef<T | null>(null)
    const [width, setWidth] = useState(fallbackWidth)

    useEffect(() => {
        const element = ref.current
        if (!element) {
            return
        }

        const updateWidth = () => {
            const nextWidth = Math.floor(element.getBoundingClientRect().width)
            if (nextWidth > 0) {
                setWidth(nextWidth)
            }
        }

        updateWidth()
        const observer = new ResizeObserver(updateWidth)
        observer.observe(element)

        return () => observer.disconnect()
    }, [])

    return [ref, width] as const
}

export default function RelatedGifs({ gifs, gifId }: { gifs: EmbedGifType[]; gifId: string }) {
    const [containerRef, width] = useElementWidth<HTMLDivElement>(320)
    const gf = useGiphyFetch()
    const fetchGifs = useCallback(
        (offset: number) =>
            gf.related(gifId, {
                limit: getRelatedGifLimit(offset),
                offset,
                rating: 'pg-13',
                type: 'gifs',
            }),
        [gf, gifId]
    )
    const onGifClick = useCallback((gif: IGif, event: SyntheticEvent<HTMLElement>) => {
        event.preventDefault()
        window.open(gif.url, '_blank')
    }, [])

    if (gifs.length === 0) {
        return null
    }

    return (
        <div ref={containerRef} className="hidden px-4 pb-24 min-[360px]:block">
            <div data-related-grid>
                <SDKGrid
                    width={Math.max(width - 32, 288)}
                    columns={3}
                    gutter={6}
                    initialGifs={gifs.slice(0, relatedGifLimit)}
                    fetchGifs={fetchGifs}
                    onGifClick={onGifClick}
                    hideAttribution
                    loader={Loader}
                    backgroundColor="transparent"
                />
            </div>
        </div>
    )
}
